/**
 * Shared orphan-card layout logic for odd-count question grids.
 *
 * Used by TeamPick / PlayerPick / CustomPick to give the trailing card
 * (in a grid-cols-2 layout with an odd number of options) a centred
 * full-row cell that's still sized to match its siblings. Centralised
 * so tweaks to the orphan sizing — or the grid `gap` it depends on —
 * change all question types together.
 *
 * Width is `calc(50% - 0.5rem)`: half the row minus half the 1rem gap
 * applied by `gap-4` on the parent grid. If that gap ever changes,
 * adjust `ORPHAN_CARD_WIDTH` here.
 */

/** Per-card flag: is this the orphan in an odd-count 2-column grid? */
export function isOrphanCard(args: {
  index: number;
  total: number;
  isSingleColumn: boolean;
}): boolean {
  const { index, total, isSingleColumn } = args;
  return !isSingleColumn && total % 2 === 1 && index === total - 1;
}

/**
 * Tailwind utility width that keeps the orphan the same size as its
 * siblings. Tied to the parent grid's `gap-4` (1rem); update both
 * together.
 */
export const ORPHAN_CARD_WIDTH = 'w-[calc(50%-0.5rem)]' as const;

/**
 * Classes applied to the orphan's grid item so it spans the row and
 * centres itself within. Excludes width (left to caller because some
 * components put it on the wrapper, others on the button directly).
 */
export const ORPHAN_CELL_LAYOUT = 'col-span-2 justify-self-center' as const;

/** Full orphan classes (cell layout + width). Convenience composition. */
export const ORPHAN_CARD_CLASSES = `${ORPHAN_CELL_LAYOUT} ${ORPHAN_CARD_WIDTH}` as const;
